import React, { useEffect, useState } from "react";
import { api, formatValue, pctColor } from "@/lib/api";
import { Loader2, Trash2, History } from "lucide-react";
import DmeTag from "@/components/DmeTag";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const verdictLabel = { side_a: "Side A wins", side_b: "Side B wins", even: "Even trade" };

export default function TradeHistory() {
  const [items, setItems] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (lg) => {
    setLoading(true);
    const url = lg ? `/trade/history?league_id=${lg}` : "/trade/history";
    return api.get(url).then(r => setItems(r.data.history || [])).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/leagues").then(r => setLeagues(r.data.leagues || [])); }, []);
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line

  const remove = async (id) => {
    await api.delete(`/trade/history/${id}`);
    setItems(items.filter(x => x.id !== id));
  };

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// HISTORY</div>
          <h1 className="font-display text-4xl font-bold" data-testid="history-heading">Trade History</h1>
          <p className="text-[var(--text-2)] mt-2">Every trade you evaluate is saved here so you can revisit your thinking.</p>
        </div>
        {leagues.length > 0 && (
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-dark w-auto text-sm" data-testid="history-league-filter">
            <option value="">All trades</option>
            {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
          </select>
        )}
      </header>

      {loading ? (
        <div className="card-bordered p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
      ) : items.length === 0 ? (
        <div className="card-bordered p-12 text-center text-[var(--text-2)]" data-testid="history-empty">
          <History className="w-8 h-8 mx-auto mb-3 text-[var(--text-3)]" />
          No trades evaluated yet. Build one in the Trade Calculator.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(t => (
            <div key={t.id} className="card-bordered p-4" data-testid={`history-item-${t.id}`}>
              {(t.league_name || t.format) && (
                <div className="flex items-center gap-2 mb-2">
                  {t.league_name && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-[var(--text-2)]">{t.league_name}</span>}
                  {t.format && <span className="text-[10px] px-2 py-0.5 rounded bg-[var(--accent)]/15 text-[var(--accent)] uppercase tracking-widest">{t.format === "tep" ? "TE Premium" : t.format === "superflex" ? "Superflex" : "1QB"}</span>}
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 grid md:grid-cols-2 gap-4 min-w-0">
                  <div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] mb-1">SIDE A · {formatValue(t.value_a)}</div>
                    <div className="text-sm">{t.side_a.map(p => p.name).join(", ")}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] mb-1">SIDE B · {formatValue(t.value_b)}</div>
                    <div className="text-sm">{t.side_b.map(p => p.name).join(", ")}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-semibold">{verdictLabel[t.winner]}</div>
                  <div className={`text-sm tabular ${pctColor(t.diff)} inline-flex items-center gap-1.5 justify-end`}>{t.diff >= 0 ? "+" : ""}{formatValue(t.diff)} <DmeTag /></div>
                  <div className="text-[10px] text-[var(--text-3)] mt-1">Fairness {t.fairness}% · {timeAgo(t.ts)}</div>
                  <button onClick={() => remove(t.id)} className="text-[var(--text-3)] hover:text-red-400 mt-2" data-testid={`history-delete-${t.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
